import * as THREE from 'three';
import type { World } from '../sim/world';
import type { Sighting } from '../sim/lookout';
import { GIVE_UP } from '../sim/lookout';
import type { Vessel } from '../sim/vessel';
import {
  METRES_PER_NM, agoText, aspect, bearingText, bearingTo, degrees, knotsText, nmText, relBearing, three,
} from './nautical';

// Keeping a glass on one ship. The player picks a sail from the In Sight
// list; the lookouts' sighting of her (see sim/lookout.ts) is written up on
// a small card, a pair of brass brackets hangs over her, and a dotted
// bearing line runs out across the water toward her. Everything comes from
// the sighting: while she's in sight the brackets ride on the ship herself,
// once she's lost they stay on the reckoned position inside a ring of
// doubt, and when the lookouts give her up the tracking ends.

const SVG = 'http://www.w3.org/2000/svg';
const LINE_POINTS = 24;

function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}): SVGElementTagNameMap[K] {
  const e = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

/** Where to find a ship's rendered pose (interpolated, so the marker follows smoothly). */
export type PoseOf = (v: Vessel) => THREE.Object3D | undefined;

export class Tracker {
  /** Vessel being tracked, or -1. */
  selected = -1;
  private readonly panel: HTMLElement;
  private readonly rows: Record<string, HTMLElement> = {};
  private readonly title: HTMLElement;
  private readonly subtitle: HTMLElement;
  private readonly overlay: SVGSVGElement;
  private readonly line: SVGPolylineElement;
  private readonly brackets: SVGPathElement;
  private readonly ring: SVGEllipseElement;
  private readonly label: SVGTextElement;
  private readonly edge: SVGGElement;
  private readonly edgeText: SVGTextElement;
  private readonly disp = new THREE.Vector3();
  private hasDisp = false;
  private textTimer = 0;
  private goneTimer = 0;
  private readonly tmp = new THREE.Vector3();
  private readonly tmp2 = new THREE.Vector3();
  private readonly camDir = new THREE.Vector3();
  private readonly pts: string[] = [];

  constructor(hudWrap: HTMLElement, private readonly world: World, private readonly onChange: (id: number) => void) {
    // --- the card
    this.panel = document.createElement('div');
    this.panel.className = 'panel tracker hidden';
    this.panel.innerHTML = `
      <div class="tr-head"><span class="stat-label">Under our glass</span><button class="tr-close" title="Stop tracking">×</button></div>
      <div class="tr-name"></div>
      <div class="tr-sub"></div>
      <dl class="tr-rows"></dl>`;
    this.title = this.panel.querySelector('.tr-name')!;
    this.subtitle = this.panel.querySelector('.tr-sub')!;
    const dl = this.panel.querySelector('.tr-rows')!;
    for (const [key, label] of [['distance', 'Distance'], ['bearing', 'Bearing'], ['lies', 'Lies'], ['heading', 'Heading'], ['speed', 'Speed'], ['status', 'Status']] as const) {
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dl.append(dt, dd);
      this.rows[key] = dd;
    }
    this.panel.querySelector('.tr-close')!.addEventListener('click', () => this.select(-1));
    hudWrap.appendChild(this.panel);

    // --- the overlay: projected onto the scene each frame
    this.overlay = svg('svg', { class: 'tracker-overlay' });
    this.line = svg('polyline', { class: 'tr-line' });
    this.ring = svg('ellipse', { class: 'tr-ring' });
    this.brackets = svg('path', { class: 'tr-brackets' });
    this.label = svg('text', { class: 'tr-label', 'text-anchor': 'middle' });
    this.edge = svg('g', { class: 'tr-edge' });
    this.edge.appendChild(svg('path', { d: 'M -7 -9 L 9 0 L -7 9 L -3 0 Z' }));
    this.edgeText = svg('text', { class: 'tr-label', 'text-anchor': 'middle', y: '24' });
    this.edge.appendChild(this.edgeText);
    this.overlay.append(this.line, this.ring, this.brackets, this.label, this.edge);
    hudWrap.appendChild(this.overlay);
    this.hideOverlay();
  }

  /** Track a vessel (or -1 to stop). Only ships the lookouts have seen can be tracked. */
  select(id: number): void {
    if (id >= 0 && !this.world.lookout.get(id)) id = -1;
    if (id === this.selected) return;
    this.selected = id;
    this.hasDisp = false;
    this.goneTimer = 0;
    this.textTimer = 0;
    this.panel.classList.toggle('hidden', id < 0);
    if (id < 0) this.hideOverlay();
    this.onChange(id);
  }

  toggle(id: number): void {
    this.select(this.selected === id ? -1 : id);
  }

  private hideOverlay(): void {
    for (const e of [this.line, this.ring, this.brackets, this.label, this.edge]) e.style.display = 'none';
  }

  update(dt: number, camera: THREE.PerspectiveCamera, poseOf: PoseOf): void {
    if (this.selected < 0) return;
    const w = this.world;
    const s = w.lookout.sightings.get(this.selected);
    const v = w.vessels.find((o) => o.id === this.selected);
    if (!s || s.firstSeen < 0) {
      this.select(-1);
      return;
    }
    if (s.gone) {
      // Say what became of her, then close the card.
      if (this.goneTimer === 0) this.writeCard(s, v);
      this.goneTimer += dt;
      this.hideOverlay();
      if (this.goneTimer > 6) this.select(-1);
      return;
    }
    this.textTimer -= dt;
    if (this.textTimer <= 0) {
      this.textTimer = 0.25;
      this.writeCard(s, v);
    }
    this.drawOverlay(dt, camera, s, v, poseOf);
  }

  // ------------------------------------------------------------ card

  private writeCard(s: Sighting, v: Vessel | undefined): void {
    const w = this.world;
    const me = w.player.ship;
    const d = Math.hypot(s.x - me.com.x, s.z - me.com.z);
    const brg = bearingTo(me.com.x, me.com.z, s.x, s.z);
    const lost = !s.visible;
    const ago = w.time - s.seen;
    const known = s.identified && v;
    this.title.textContent = known ? `“${v.name}”` : 'A strange sail';
    this.subtitle.textContent = known
      ? `${v.cls.name} · ${v.faction === 'navy' ? 'navy colours' : v.faction === 'merchant' ? 'merchant colours' : v.faction === 'pirate' ? 'the black flag' : ''}`
      : lost ? 'never near enough to make out her colours' : 'too far off to make out her colours';
    const r = this.rows;
    if (s.gone) {
      r.distance!.textContent = '—';
      r.bearing!.textContent = '—';
      r.lies!.textContent = '—';
      r.heading!.textContent = '—';
      r.speed!.textContent = '—';
      r.status!.textContent = v && v.state === 'sunk' ? 'Gone down' : 'Lost — the lookouts have given her up';
      this.panel.classList.add('lost');
      return;
    }
    this.panel.classList.toggle('lost', lost);
    const doubt = s.uncertainty / METRES_PER_NM;
    r.distance!.textContent = lost ? `≈ ${nmText(d)} ± ${doubt < 0.1 ? doubt.toFixed(2) : doubt.toFixed(1)}` : nmText(d);
    r.bearing!.textContent = `${lost ? '≈ ' : ''}${bearingText(brg)}`;
    r.lies!.textContent = `${relBearing(brg - me.heading)}${s.watched > 3 && !lost ? `, ${aspect(s.heading, brg)}` : ''}`;
    const sp = Math.hypot(s.vx, s.vz);
    if (s.watched < 3 && !lost) {
      r.heading!.textContent = 'taking her bearings…';
      r.speed!.textContent = 'judging…';
    } else {
      r.heading!.textContent = `${three(degrees(s.heading))}${lost ? ' when last seen' : ''}`;
      r.speed!.textContent = `${lost ? '≈ ' : ''}${knotsText(sp)}${lost ? ' when last seen' : ''}`;
    }
    r.status!.textContent = lost ? `Lost to sight ${agoText(ago)} — reckoned${w.time - s.seen > GIVE_UP * 0.7 ? ', soon given up' : ''}` : this.status(s, v, d, sp);
  }

  private status(s: Sighting, v: Vessel | undefined, d: number, speed: number): string {
    // What can be seen of her plight from here.
    if (v && d < 4000) {
      if (v.state === 'sinking') return 'Going down';
      if (v.state === 'struck') return 'Has struck her colours';
      if (v.state === 'captured') return 'Taken as a prize';
      if (v.boardingWith >= 0) return 'Grappled — boarding';
      if (v.fire.worst > 0.3 && d < 3000) return 'On fire';
    }
    const dismasted = v && d < 3500 && v.ship.sails.masts.length < v.cls.masts.length ? 'Dismasted, ' : '';
    if (s.watched < 3) return `${dismasted}Watching her`;
    if (speed < 0.4) return `${dismasted}Hove to`;
    const rr = s.rangeRate;
    if (rr < -0.3) {
      const toGunshot = d > 1000 ? (d - 900) / -rr : 0;
      return `${dismasted}Closing${toGunshot > 0 ? ` — within gunshot in about ${Math.max(1, Math.round(toGunshot / 60))} min` : ''}`;
    }
    if (rr > 0.3) return `${dismasted}Moving away`;
    return `${dismasted}Holding her distance`;
  }

  // ------------------------------------------------------------ overlay

  private drawOverlay(dt: number, camera: THREE.PerspectiveCamera, s: Sighting, v: Vessel | undefined, poseOf: PoseOf): void {
    const w = this.world;
    const W = window.innerWidth, H = window.innerHeight;
    this.overlay.setAttribute('viewBox', `0 0 ${W} ${H}`);
    const sy = v ? v.ship.sy : 1;
    // Where the brackets belong: on the ship herself while she's in sight
    // (her rendered pose, so they ride her smoothly), on the reckoning when not.
    const pose = s.visible && v ? poseOf(v) : undefined;
    const target = this.tmp;
    if (pose) pose.localToWorld(target.set(0, 12 * sy, 0));
    else target.set(s.x, 12 * sy, s.z);
    if (!this.hasDisp) {
      this.disp.copy(target);
      this.hasDisp = true;
    } else this.disp.lerp(target, 1 - Math.exp(-dt * 7));

    const me = w.player.ship;
    const dist = Math.hypot(this.disp.x - me.com.x, this.disp.z - me.com.z);
    const focal = H / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2));
    const camDist = camera.position.distanceTo(this.disp);
    const camDir = camera.getWorldDirection(this.camDir);
    const cp = camera.position;
    const ahead = (x: number, y: number, z: number) => (x - cp.x) * camDir.x + (y - cp.y) * camDir.y + (z - cp.z) * camDir.z > 0.5;
    const inFront = ahead(this.disp.x, this.disp.y, this.disp.z);
    const p = this.tmp2.copy(this.disp).project(camera);
    const sx = (p.x * 0.5 + 0.5) * W, sy2 = (-p.y * 0.5 + 0.5) * H;
    const onScreen = inFront && sx > -20 && sx < W + 20 && sy2 > -20 && sy2 < H + 20;
    const lost = !s.visible;
    this.overlay.classList.toggle('lost', lost);

    // Brackets over her (or the ring of doubt round the reckoning).
    if (onScreen) {
      const half = Math.max(16, Math.min(160, ((24 * (v ? v.ship.sx : 1)) / camDist) * focal * 0.55));
      const hh = half * 0.75, c = Math.min(10, half * 0.35);
      const x0 = sx - half, x1 = sx + half, y0 = sy2 - hh, y1 = sy2 + hh;
      this.brackets.setAttribute('d',
        `M${x0},${y0 + c} L${x0},${y0} L${x0 + c},${y0} M${x1 - c},${y0} L${x1},${y0} L${x1},${y0 + c} ` +
        `M${x1},${y1 - c} L${x1},${y1} L${x1 - c},${y1} M${x0 + c},${y1} L${x0},${y1} L${x0},${y1 - c}`);
      this.brackets.style.display = '';
      if (lost) {
        const rr = Math.max(half, (s.uncertainty / camDist) * focal);
        this.ring.setAttribute('cx', String(sx));
        this.ring.setAttribute('cy', String(sy2));
        this.ring.setAttribute('rx', String(rr));
        this.ring.setAttribute('ry', String(rr * 0.35 + 6));
        this.ring.style.display = '';
      } else this.ring.style.display = 'none';
      this.label.setAttribute('x', String(sx));
      this.label.setAttribute('y', String(y1 + 16));
      this.label.textContent = `${s.identified && v ? v.name : 'Strange sail'} · ${lost ? '≈ ' : ''}${nmText(dist)}`;
      this.label.style.display = '';
      this.edge.style.display = 'none';
    } else {
      // Off the edge of the view: a small pointer at the border toward her.
      this.brackets.style.display = this.ring.style.display = this.label.style.display = 'none';
      let dx = p.x, dy = -p.y;
      if (!inFront) {
        dx = -dx;
        dy = -dy;
      }
      const len = Math.hypot(dx, dy) || 1;
      dx /= len;
      dy /= len;
      const m = 34;
      const k = Math.min((W / 2 - m) / Math.max(Math.abs(dx), 1e-3), (H / 2 - m) / Math.max(Math.abs(dy), 1e-3));
      const ex = W / 2 + dx * k, ey = H / 2 + dy * k;
      this.edge.setAttribute('transform', `translate(${ex},${ey})`);
      (this.edge.firstChild as SVGPathElement).setAttribute('transform', `rotate(${(Math.atan2(dy, dx) * 180) / Math.PI})`);
      this.edgeText.textContent = nmText(dist);
      this.edge.style.display = '';
    }

    // The bearing line, laid on the water from our side out toward her.
    if (dist > 150 && dist < 9 * METRES_PER_NM) {
      const pts = this.pts;
      pts.length = 0;
      const ax = me.com.x, az = me.com.z;
      const start = Math.min(40, dist * 0.2);
      const end = Math.max(start + 1, dist - Math.min(60, dist * 0.15));
      const ux = (this.disp.x - ax) / dist, uz = (this.disp.z - az) / dist;
      const q = this.tmp2;
      for (let i = 0; i <= LINE_POINTS; i++) {
        // Denser near us, where perspective spreads the line out.
        const t = i / LINE_POINTS;
        const r = start + (end - start) * t * t;
        q.set(ax + ux * r, 0.6, az + uz * r);
        if (!ahead(q.x, q.y, q.z)) {
          if (pts.length) break;
          continue;
        }
        q.project(camera);
        pts.push(`${((q.x * 0.5 + 0.5) * W).toFixed(1)},${((-q.y * 0.5 + 0.5) * H).toFixed(1)}`);
      }
      if (pts.length > 1) {
        this.line.setAttribute('points', pts.join(' '));
        this.line.style.display = '';
      } else this.line.style.display = 'none';
    } else this.line.style.display = 'none';
  }
}
